import React from 'react';
import I18n from 'i18n-js';
import { RCCallout, RCIcon, useRCPopover } from '@reverbdotcom/cadence/components';
import type { UtilityVariant } from '@reverbdotcom/cadence/components/RCCallout/RCCallout';
import { ChevronRightIcon } from '@reverbdotcom/cadence/icons/react';
import { NotificationLink } from './notification_link';
import {
  NotificationActionKey,
  NotificationSeverity,
  NotificationsCenterQuery,
} from '@reverbdotcom/commons/src/gql/graphql';
import { clickedNotificationEvent } from './notification_event_props';
import { useMarkActionRead } from './notifications_read_state';

type NotificationAction = NotificationsCenterQuery['notificationActions']['actions'][number];

// Only an urgent action earns the loud treatment. Two shipping promos rolled up
// shouldn't read like a payout is about to fail.
const SEVERITY_VARIANTS: Record<NotificationSeverity, UtilityVariant> = {
  [NotificationSeverity.URGENT]: 'loud-ultralight',
  [NotificationSeverity.NORMAL]: 'notice-ultralight',
  [NotificationSeverity.INFO]: 'info-ultralight',
};

interface IProps {
  rollup: NotificationAction | null;
  onOpenRollup: () => void;
  // Held by the popover so leaving the rollup can put focus back on the control that
  // opened it. Only the rollup banner takes it - a lone action is a link straight out.
  rollupTriggerRef?: React.Ref<HTMLButtonElement>;
}

export default function ActionsSection({ rollup, onOpenRollup, rollupTriggerRef }: IProps) {
  const markActionRead = useMarkActionRead();

  if (!rollup) return null;

  // The server builds the ROLLUP key when it folds several actions into one
  // banner. Its href is the Reverb root on web, since the list lives in this
  // panel rather than on a page of its own, so open the pane instead.
  return (
    <div className="notifications-center__actions">
      {rollup.key === NotificationActionKey.ROLLUP
        ? <ActionsRollupBanner action={rollup} onClick={onOpenRollup} buttonRef={rollupTriggerRef} />
        : <ActionBanner action={rollup} onMarkRead={markActionRead} />
      }
    </div>
  );
}

function bannerCopy(action: NotificationAction) {
  return I18n.t('discovery.notificationsCenter.actions.bannerCopy', {
    title: action.title,
    description: action.description,
  });
}

function NagBox({ severity, children }: { severity: NotificationSeverity; children: React.ReactNode }) {
  return (
    <RCCallout
      themeType="utility"
      variant={SEVERITY_VARIANTS[severity] ?? SEVERITY_VARIANTS[NotificationSeverity.INFO]}
      border="leading-accent"
      compact
    >
      <span className="notifications-center__nag__row">
        <span className="notifications-center__nag__copy">{children}</span>
        <span className="notifications-center__nag__chevron">
          <RCIcon icon={<ChevronRightIcon />} size="4" />
        </span>
      </span>
    </RCCallout>
  );
}

export function ActionBanner({ action, onMarkRead }: {
  action: NotificationAction;
  onMarkRead: (action: NotificationAction) => void;
}) {
  // The panel is an RCPopover on desktop, which a click on its own content won't
  // dismiss. A no-op inside the mobile drawer, which the navigation closes anyway.
  const { close } = useRCPopover();

  return (
    <NotificationLink
      to={action.href}
      className="notifications-center__nag"
      clickEvent={clickedNotificationEvent(action.eventTracking)}
      onClick={() => { onMarkRead(action); close(); }}
    >
      <NagBox severity={action.severity}>{bannerCopy(action)}</NagBox>
    </NotificationLink>
  );
}

export function ActionsRollupBanner({ action, onClick, buttonRef }: {
  action: NotificationAction;
  onClick: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onClick}
      className="notifications-center__nag"
    >
      <NagBox severity={action.severity}>{bannerCopy(action)}</NagBox>
    </button>
  );
}
